import { privacy } from "@redotech/schema-privacy";
import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod";
import {
  countNameTemplateResults,
  expandNameTemplate,
  NameTemplateError,
  parseNameTemplate,
} from "./name-template";
import {
  BinPrioritySchema,
  PersistentStorageLocationType,
  PersistentStorageLocationTypeSchema,
  StorageLocationType,
} from "./schema/storage-location";

export enum BinNamingField {
  ROW = "ROW",
  RACK = "RACK",
  SHELF = "SHELF",
  BIN = "BIN",
}

export enum SEPARATOR {
  DASH = "-",
}

export enum BinDistributionMode {
  DISTRIBUTE = "DISTRIBUTE",
  DUPLICATE = "DUPLICATE",
}

export const BinFieldUsedSchema = z
  .record(
    createEnum(BinNamingField, { enumName: "BinNamingField" }).meta({
      privacy: privacy.nonPii,
    }),
    z.boolean().meta({ privacy: privacy.nonPii }),
  )
  .meta({ privacy: privacy.structured });

export const BinNamingSettingsSchema = z
  .object({
    binFieldUsed: BinFieldUsedSchema,
    separator: createEnum(SEPARATOR, { enumName: "BinSeparator" }).meta({
      privacy: privacy.nonPii,
    }),
    minDigits: z.number().nullish().meta({ privacy: privacy.nonPii }),
    binDistributionMode: createEnum(BinDistributionMode, {
      enumName: "BinDistributionMode",
    })
      .nullish()
      .meta({ privacy: privacy.nonPii }),
  })
  .meta({ privacy: privacy.structured });
export type BinNamingSettings = z.infer<typeof BinNamingSettingsSchema>;

// =============================================================================
// Advanced Template-Based Bin Naming
// =============================================================================

/**
 * Advanced bin naming template.
 *
 * The template string uses a simple pattern language:
 * - {ROW} - replaced with the row name from the rack
 * - {RACK} - replaced with the rack name
 * - {ROW:N} - replaced with row name, padded to N digits if numeric
 * - {RACK:N} - replaced with rack name, padded to N digits if numeric
 * - {start-end} - generates a sequence (numeric or alphabetic)
 *
 * Variable padding:
 * - {RACK:2} - pads numeric rack names to 2 digits (e.g., "1" → "01", "12" → "12")
 * - {ROW:3} - pads numeric row names to 3 digits (e.g., "5" → "005")
 * - Non-numeric values are not padded (e.g., "A" stays "A")
 *
 * Sequence patterns:
 * - {1-50} - numeric sequence 1, 2, 3, ..., 50
 * - {001-050} - zero-padded numeric sequence 001, 002, ..., 050
 * - {A-Z} - alphabetic sequence A, B, C, ..., Z
 * - {50-1} - descending numeric sequence 50, 49, ..., 1
 * - {Z-A} - descending alphabetic sequence Z, Y, ..., A
 *
 * Examples:
 * - "{ROW}-{RACK}-{001-010}-{A-E}" with ROW="A", RACK="1"
 *   → A-1-001-A, A-1-001-B, ..., A-1-010-E
 * - "{ROW}-{RACK:2}-{01-10}" with ROW="A", RACK="5"
 *   → A-05-01, A-05-02, ..., A-05-10
 * - "{RACK}{01-99}" with RACK="R1"
 *   → R101, R102, ..., R199
 */
const AdvancedBinNamingTemplateSchema_ = z.object({
  template: z
    .string()
    .meta({ privacy: privacy.nonPii })
    .describe("Template string with variables and sequence patterns"),
});
export const AdvancedBinNamingTemplateSchema: z.ZodType<AdvancedBinNamingTemplate> =
  AdvancedBinNamingTemplateSchema_;
export interface AdvancedBinNamingTemplate extends z.infer<
  typeof AdvancedBinNamingTemplateSchema_
> {}

const AdvancedBinNamingTemplateWithTypeSchema_ = z
  .object({
    template: z.string().meta({ privacy: privacy.nonPii }),
    type: PersistentStorageLocationTypeSchema.meta({ privacy: privacy.nonPii }),
    isSellable: z.boolean().nullish().meta({ privacy: privacy.nonPii }),
    priority: BinPrioritySchema.nullish().meta({ privacy: privacy.nonPii }),
  })
  .meta({ privacy: privacy.structured });
export const AdvancedBinNamingTemplateWithTypeSchema: z.ZodType<AdvancedBinNamingTemplateWithType> =
  AdvancedBinNamingTemplateWithTypeSchema_;
export interface AdvancedBinNamingTemplateWithType extends z.infer<
  typeof AdvancedBinNamingTemplateWithTypeSchema_
> {}

const AdvancedBinNamingSettingsSchema_ = z
  .object({
    enabled: z
      .boolean()
      .meta({ privacy: privacy.nonPii })
      .describe("Whether advanced template-based naming is enabled"),
    defaultTemplate: AdvancedBinNamingTemplateSchema.nullish()
      .meta({ privacy: privacy.structured })
      .describe("Deprecated legacy default. Use defaultTemplates instead."),
    defaultTemplates: z
      .array(
        AdvancedBinNamingTemplateWithTypeSchema.meta({
          privacy: privacy.structured,
        }),
      )
      .nullish()
      .meta({ privacy: privacy.structured })
      .describe(
        "Default templates used when a rack doesn't specify its own. Each entry carries its own storage-location type.",
      )
      .meta({ privacy: privacy.structured }),
  })
  .meta({ privacy: privacy.structured });
export const AdvancedBinNamingSettingsSchema: z.ZodType<AdvancedBinNamingSettings> =
  AdvancedBinNamingSettingsSchema_;
export interface AdvancedBinNamingSettings extends z.infer<
  typeof AdvancedBinNamingSettingsSchema_
> {}

// =============================================================================
// Template Parsing and Generation Utilities
// =============================================================================

const BIN_TEMPLATE_VARIABLES = ["ROW", "RACK"];
const MAX_BINS_PER_TEMPLATE = 10_000;

export function generateBinNamesFromTemplate(
  template: AdvancedBinNamingTemplate,
  rowName: string,
  rackName: string,
): string[] {
  return expandNameTemplate({
    template: template.template,
    variables: { ROW: rowName, RACK: rackName },
    maxResults: MAX_BINS_PER_TEMPLATE,
    allowUnknownPatterns: true,
  });
}

/**
 * Validates a bin naming template string.
 * Returns null if valid, or an error message string if invalid.
 */
export function validateBinNamingTemplate(template: string): string | null {
  if (!template || template.trim() === "") {
    return "Template cannot be empty";
  }
  try {
    const segments = parseNameTemplate(template, BIN_TEMPLATE_VARIABLES);
    for (const segment of segments) {
      if (segment.type === "literal" && segment.value.includes("{")) {
        const match = segment.value.match(/\{([^}]*)\}/);
        if (match) {
          return `Invalid pattern: {${match[1]}}. Use {ROW}, {RACK}, or sequences like {1-10}, {A-Z}`;
        }
      }
    }
    if (countNameTemplateResults(segments) > MAX_BINS_PER_TEMPLATE) {
      return "Template would generate more than 10,000 bins. Please use smaller ranges.";
    }
    return null;
  } catch (error) {
    if (error instanceof NameTemplateError) {
      return error.message;
    }
    throw error;
  }
}

/**
 * Returns the count of bins that would be generated from a template.
 */
export function countBinsFromTemplate(template: string): number {
  return countNameTemplateResults(
    parseNameTemplate(template, BIN_TEMPLATE_VARIABLES),
  );
}

/**
 * Returns the count of each sequence in a template.
 * Used to calculate which "level" (first sequence index) a bin belongs to.
 *
 * For example, template "{ROW}-{RACK}-{1-5}-{A-D}" returns [5, 4]
 * (5 values in first sequence, 4 in second)
 */
export function getSequenceCounts(template: string): number[] {
  return parseNameTemplate(template, BIN_TEMPLATE_VARIABLES)
    .filter((segment) => segment.type === "sequence")
    .map((segment) => segment.sequence.count);
}

export function getFirstSequenceInfo(
  template: string,
): { rawPattern: string; values: string[] } | null {
  const first = parseNameTemplate(template, BIN_TEMPLATE_VARIABLES).find(
    (segment) => segment.type === "sequence",
  );
  if (first?.type !== "sequence") {
    return null;
  }
  return {
    rawPattern: first.value,
    values: expandNameTemplate({
      template: `{${first.value}}`,
      maxResults: MAX_BINS_PER_TEMPLATE,
    }),
  };
}

export function replaceFirstSequenceInTemplate(
  template: string,
  newPattern: string,
): string {
  const regex = /\{([^}]+)\}/g;
  let match;
  while ((match = regex.exec(template)) !== null) {
    if (
      parseNameTemplate(match[0], BIN_TEMPLATE_VARIABLES).some(
        (segment) => segment.type === "sequence",
      )
    ) {
      return (
        template.slice(0, match.index) +
        `{${newPattern}}` +
        template.slice(match.index + match[0].length)
      );
    }
  }
  return template;
}

export function convertLegacyToMultiTemplates({
  template,
  ranges,
  rackBinType,
}: {
  template: string;
  ranges:
    | readonly {
        start: number;
        end: number;
        type: PersistentStorageLocationType;
      }[]
    | null
    | undefined;
  rackBinType: PersistentStorageLocationType | null | undefined;
}): AdvancedBinNamingTemplateWithType[] | null {
  if (validateBinNamingTemplate(template)) {
    return null;
  }

  const sequenceCounts = getSequenceCounts(template);
  const first = sequenceCounts[0] ?? 0;
  const rangeList = ranges ?? [];

  if (rackBinType) {
    return [{ template, type: rackBinType }];
  }

  if (first === 0) {
    const type = rangeList.find(
      (range) => range.start <= 0 && range.end >= 0,
    )?.type;
    return [{ template, type: type ?? StorageLocationType.BIN }];
  }

  if (rangeList.length === 0) {
    return [{ template, type: StorageLocationType.BIN }];
  }

  const sorted = [...rangeList].sort((a, b) => a.start - b.start);
  for (let i = 1; i < sorted.length; i++) {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const prev = sorted[i - 1]!;
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const cur = sorted[i]!;
    if (cur.start <= prev.end) {
      return null;
    }
  }

  const typeAtIndex: PersistentStorageLocationType[] = [];
  for (let i = 0; i < first; i++) {
    const hit = sorted.find((r) => i >= r.start && i <= r.end);
    typeAtIndex.push(hit?.type ?? StorageLocationType.BIN);
  }

  const distinct = new Set(typeAtIndex);
  if (distinct.size === 1) {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    return [{ template, type: typeAtIndex[0]! }];
  }

  const info = getFirstSequenceInfo(template);
  if (!info || info.values.length !== first) {
    return null;
  }

  const templates: AdvancedBinNamingTemplateWithType[] = [];
  let runStart = 0;
  for (let i = 1; i <= typeAtIndex.length; i++) {
    if (i === typeAtIndex.length || typeAtIndex[i] !== typeAtIndex[runStart]) {
      const runEnd = i - 1;
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      const runType = typeAtIndex[runStart]!;
      const newPattern = `${info.values[runStart]}-${info.values[runEnd]}`;
      const newTemplate = replaceFirstSequenceInTemplate(template, newPattern);
      templates.push({ template: newTemplate, type: runType });
      runStart = i;
    }
  }

  return templates;
}

/**
 * Calculates the first sequence index from a flat bin index.
 */
export function getFirstSequenceIndex(
  flatIndex: number,
  sequenceCounts: number[],
): number {
  if (sequenceCounts.length === 0) {
    return 0;
  }

  // Product of all sequences after the first
  const divisor = sequenceCounts
    .slice(1)
    .reduce((acc, count) => acc * count, 1);
  return Math.floor(flatIndex / divisor);
}
